import * as React from "react";
import formatDate = require("dateformat");
import {IProxySearchResponse} from "responses";
import {Repeater} from "@components/repeater";
import {PoweredByFooter} from "./powered-by-footer";
import { useLoadingState } from "@hooks/use-loading-state";
import { ProxyService, isApiError } from "@modules/api/api";
import { MaybeLoadingSpinner } from "@components/maybe-loading-spinner";
import { Unless } from "@components/unless";
import {MaybeError} from "@components/maybe-error";
import { getHeaderClass } from "./get-header-class";

type SearchType = "email" | "order";

interface Props {
    themeColor: string
    shopUrl: string
    searchTerm: string
    searchType: SearchType
    service: ProxyService
    onNavigateToSearch: () => void
    onNavigateToOrder: (orderShopifyId: number) => void
}

export function SearchResultsSection(props: Props): JSX.Element {
    const [results, setResults] = React.useState<IProxySearchResponse[]>([]);
    const [{ loading, error }, setLoadingState] = useLoadingState(true);

    const backToSearch = (event: React.MouseEvent<any>) => {
        event.preventDefault();
        props.onNavigateToSearch();
    };

    const selectItem = (item: IProxySearchResponse) => (event: React.MouseEvent<any>) => {
        event.preventDefault();
        props.onNavigateToOrder(item.id);
    }

    React.useEffect(() => {
        async function beginSearch() {
            setLoadingState(true);

            try {
                const results = await props.service.ListCustomerOrdersAsync(props.searchTerm, props.searchType);

                if (results.length === 0) {
                    setLoadingState(props.searchType === "email"
                        ? "No orders found for that email address."
                        : "Order " + props.searchTerm + " wasn't found."
                    );
                } else {
                    setResults(results);
                    setLoadingState(false);
                }
            } catch (e: any) {
                console.error("Failed to list customer orders", e);

                const message = isApiError(e) && e.HumanReadable
                    ? e.message
                    : "Something went wrong and the search could not be completed. Please try again.";

                setLoadingState(message);
            }
        }

        beginSearch();
    }, [props.searchTerm, props.searchType]);

    const headerClass = getHeaderClass(props.shopUrl);

    return (
        <section className="m-col-18-24">
            <div className="box" style={{ borderTop: "6px solid " + props.themeColor }}>
                <form className="panel active">
                    <div className={headerClass}>
                        <h4>
                            {loading ? "Searching for orders" : "Which order are you looking for?"}
                        </h4>
                    </div>
                    <div className="body">
                        <MaybeLoadingSpinner 
                            loading={loading} 
                            text={props.searchType === "email"
                                ? "Looking for orders from " + decodeURIComponent(props.searchTerm)
                                : "Please wait."} 
                        />
                        <MaybeError error={error} />
                        <Unless condition={loading}>
                            <ul className="orders">
                                <Repeater source={results}>
                                    {o => (
                                        <li key={o.id}>
                                            <a href="#" onClick={selectItem(o)}>
                                                {`${o.displayId} on ${formatDate(new Date(o.dateCreated), "mmm dd, yyyy")} — ${o.lineItemSummary}`}
                                            </a>
                                        </li>
                                    )}
                                </Repeater>
                            </ul>
                        </Unless>
                    </div>
                    <div className="footer">
                        <div className="ms-row vc">
                            <div className="m-col-12-24">
                                <PoweredByFooter shopUrl={props.shopUrl} />
                            </div>
                            <Unless condition={loading}>
                                <div className="m-col-12-24 text-right">
                                    <a href="#" onClick={backToSearch}>
                                        {"Back to search."}
                                    </a>
                                </div>
                            </Unless>
                        </div>
                    </div>
                </form>
            </div>
        </section>
    );
}
